import SurveysService from "@/services/SurveysService";
import AdministrationService from "@/services/AdministrationService";
import DashboardService from "@/services/DashboardService-bmw";
import EventBus from "@/services/EventBus";
import indicesData from "@/data/indices.json";

export default {
  namespaced: true,

  state: {
    clients: [],
    structure: {},
    structures: [],
    isClientReady: false,
    appLoading: false,
    hasData: false,
    brandIds: [],
    countryCodes: [],
    filters: undefined,
  },

  getters: {
    appLoading: state => state.appLoading,
    client: state => {
      if (!state.isClientReady || !state.clients || state.clients.length === 0) {
        return null;
      }
      const clientId = state.structure?.id?.substring(1); // Remove 'C' prefix
      return state.clients.find(c => c.ClientId.toString() === clientId) || null;
    },
    hasData: state => state.hasData,
    clients: state => state.clients || [],
    brandIds: state => state.brandIds,
    countryCodes: state => state.countryCodes,
    filters: state => state.filters,
    getFiltersCopy: state => state.filters ? JSON.parse(JSON.stringify(state.filters)) : undefined,
    isClientReady: state => state.isClientReady,
    project: state => {
      const firstChild = state.structure.children?.[0];
      return firstChild?.id?.startsWith('P') ? firstChild : null;
    },
    structure: state => state.structure,
    structures: state => state.structures,
    indices: (state, getters) => {
      const client = getters.client;
      if (!client?.Thresholds?.length) return [];

      const clientThresholds = [...client.Thresholds].reverse();
      const firstIndex = indicesData[0];
      const firstIndexMergedThresholds = firstIndex.thresholds.map((t, i) => {
        const ct = clientThresholds[i];
        if (!ct) return { ...t };
        return {
          ...t,
          min: ct.Minimum,
          max: ct.Maximum,
          color: ct.Color,
          icons: ct.Icons,
        };
      });

      // Alle anderen Indizes: Nur die Farben aus clientThresholds zuweisen, min/max bleiben gleich
      const otherIndicesMergedThresholds = indicesData.slice(1).map(index => {
        const thresholds = index.thresholds.map((t, i) => {
          const ct = clientThresholds[i];
          if (!ct) return { ...t };
          return {
            ...t,
            color: ct.Color,
            icons: ct.Icons,
          };
        });
        return { ...index, thresholds };
      });


      return [
        { ...firstIndex, thresholds: firstIndexMergedThresholds },
        ...otherIndicesMergedThresholds,
      ];
    },
    surveyIds: state => {
      if (!state.isClientReady) return [];
      try {
        if (state.structure && state.structure.children[0]?.id.startsWith('P')) {
          if (!state.structure.children[0].children) return [];
          return state.structure.children[0].children.map(project => project.Survey.SurveyId);
        }
        return state.structure.children.map(survey => survey.Survey.SurveyId);
      } catch (error) {
        return [];
      }
    },
  },

  mutations: {
    SET_BRANDIDS(state, brandIds) {
      state.brandIds = brandIds;
    },
    SET_CLIENTS(state, clients) {
      state.clients = clients;
    },
    SET_COUNTRYCODES(state, countryCodes) {
      state.countryCodes = countryCodes;
    },
    SET_FILTERS(state, filters) {
      state.filters = filters;
    },
    SET_HASDATA(state, hasData) {
      state.hasData = hasData;
    },
    SET_ISCLIENTREADY(state, isClientReady) {
      const wasReady = state.isClientReady;
      state.isClientReady = isClientReady;
      if (!wasReady && isClientReady) {
        EventBus.$emit('app.ready');
      }
    },
    SET_APPLOADING(state, appLoading) {
      state.appLoading = appLoading;
    },
    SET_STRUCTURE(state, structure) {
      state.structure = structure;
    },
    CHANGE_STRUCTURE(state, clientId) {
      const newStructure = state.structures.find(x => x.id === `C${clientId}`);
      if (newStructure) {
        state.structure = newStructure;
      }
    },
    SET_STRUCTURES(state, structures) {
      state.structures = structures;
    },
  },

  actions: {
    setAppLoading({ commit }, appLoading) {
      commit('SET_APPLOADING', appLoading);
    },
    setHasData({ commit }, hasData) {
      commit('SET_HASDATA', hasData);
    },
    async fetchClients({ commit, dispatch }) {
       AdministrationService.getClientSettings(null)
        .then(response => {
          commit('SET_CLIENTS', response.data);
        })
        .catch(error => {
          log.error("STO: Error while receiving client settings: " + error);
          this.isBusy = false;
        });
    },

    async fetchStructure({ commit, state, rootGetters }) {
      if(state.structure && state.structure.id)
        return;

      commit('SET_ISCLIENTREADY', false);
      const localizationId = rootGetters['auth/localizationId'];
      const clientId = rootGetters['auth/clientId'];
      const structures = await SurveysService.getSurveyStructure(
        localizationId, false, false);
      commit('SET_STRUCTURES', structures.data);
      commit('SET_STRUCTURE', structures.data.find(x => x.id === `C${clientId}`) || undefined);

      if (state.filters)
        commit('SET_ISCLIENTREADY', true);
    },

    async fetchFilters({ commit, state, rootGetters }) {
      if (state.filters)
        return;

      const localizationId = rootGetters['auth/localizationId'];
      const emptyFilters = await DashboardService.postOptions({ api: '2021', "localization-id": localizationId }, null);

      const clientId = emptyFilters.data._visuals[0].ClientBmw;
      commit('auth/SET_CLIENT_ID', clientId, { root: true });

      const brandIds = emptyFilters.data.Brand[0].children[0].children.map(group => group.id.substring(1));
      commit('SET_BRANDIDS', brandIds);

      DashboardService.postOptions({ api: '2021', Brand: brandIds, "localization-id": localizationId }, null)
        .then(filters => {
          const countryCodes = [...new Set(
            filters.data.ClientLocations.map(location =>
              location.label.substring(0, 2)
            )
          )];
          commit('SET_COUNTRYCODES', countryCodes);
          let fltr = filters.data;
          commit('SET_FILTERS', fltr);
          commit('SET_ISCLIENTREADY', true);
        }).catch(() => {
          log.error("STO: Failed to load country codes AND FILTERS");
        });
    }
  },
};
